"use strict";
// The store catalogue — which bookable apps exist, which capability keys each
// one grants, and how they are grouped for presentation (PRD-816, issue #816).
//
// This is the capability half of a two-level catalogue that deliberately
// straddles the package split (GEN-004): what an app IS lives here, beside the
// tier truth it extends; what an app COSTS lives in
// `crm-subscription-billing-domain`, beside the rest of the euro arithmetic.
// Neither package imports the other; the two halves are joined by the app id
// and kept honest by completeness tests on both sides.
//
// A store DOMAIN is a sales grouping, not a navigation section: it never appears
// in `NAV_CATALOG`, and the areas an app unlocks keep appearing in the sidebar
// and launcher gated by their own feature key (FE-012). The two groupings are
// allowed to differ, and do — the assistant unlocks no navigable area at all.
//
// Domains are never persisted. Only the installed app ids are stored, so
// re-grouping apps between domains later is a code change with no migration.
//
// Prose is resolved via Transloco on the client (FE-003), so this file carries
// i18n KEYS only, never translated strings.
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_CATALOG = exports.APP_IDS = exports.STORE_DOMAINS = exports.STORE_DOMAIN_IDS = void 0;
exports.isAppId = isAppId;
exports.appById = appById;
exports.featuresFromApps = featuresFromApps;
exports.supportingGrantReason = supportingGrantReason;
/** The store's sales groupings, in display order. */
exports.STORE_DOMAIN_IDS = ['ai', 'integrations', 'finance', 'goods', 'crm', 'construction'];
const DOMAIN_PREFIX = 'store.domains.';
const APP_PREFIX = 'store.apps.';
function domainKey(domainId, leaf) {
    return `${DOMAIN_PREFIX}${domainId}.${leaf}`;
}
function appKey(appId, leaf) {
    return `${APP_PREFIX}${appId}.${leaf}`;
}
/**
 * The domain cards of the store, in display order. One entry per grouping —
 * a domain with a single app renders a heading and that app, which is the state
 * this delivery ships.
 */
exports.STORE_DOMAINS = [
    {
        domainId: 'ai',
        order: 0,
        icon: 'pi pi-sparkles',
        nameKey: domainKey('ai', 'name'),
        descriptionKey: domainKey('ai', 'description'),
    },
    {
        domainId: 'integrations',
        order: 1,
        icon: 'pi pi-share-alt',
        nameKey: domainKey('integrations', 'name'),
        descriptionKey: domainKey('integrations', 'description'),
    },
    // Bundle-only (PRD-852): sold as a whole, its apps carry no individual price.
    // App-less until the Steuerberater-Handover app ships (Phase 10) — the store
    // view drops an app-less domain, so nothing is visible or bookable yet.
    {
        domainId: 'finance',
        order: 2,
        icon: 'pi pi-calculator',
        nameKey: domainKey('finance', 'name'),
        descriptionKey: domainKey('finance', 'description'),
    },
    // Bundle-only (PRD-854/PRD-1845): the package the market ("Warenwirtschaft") is
    // used to buying as a whole, and the card that has to SHOW that whole — one
    // tile per area the sidebar reveals, because the domain card is the only
    // place a buyer can see what the one price contains (issue #1013).
    {
        domainId: 'goods',
        order: 3,
        icon: 'pi pi-box',
        nameKey: domainKey('goods', 'name'),
        descriptionKey: domainKey('goods', 'description'),
    },
    // PRD-1632: the apps that extend the CRM itself. Team-Chat is its only app
    // for now and is booked and priced on its own — the card is presentation,
    // not a bundle. Chat and video with customers and partners (IN-021) are
    // expected to join it, which is why it is the CRM's card and not one for
    // communication.
    {
        domainId: 'crm',
        order: 4,
        icon: 'pi pi-users',
        nameKey: domainKey('crm', 'name'),
        descriptionKey: domainKey('crm', 'description'),
    },
    // PRD-2133: the first industry domain, holding the first connected app. Priced
    // per app, not bundle-only, until a second construction app asks the question.
    {
        domainId: 'construction',
        order: 5,
        icon: 'pi pi-building',
        nameKey: domainKey('construction', 'name'),
        descriptionKey: domainKey('construction', 'description'),
    },
];
/** The bookable app ids — the store's purchase units and the only stored thing. */
exports.APP_IDS = [
    'ai-chat',
    'ai-agent',
    'ai-reporting',
    'external-api',
    'tax-advisor-handover',
    'tax-advisor-access',
    'inventory',
    'purchase-requests',
    'purchase-orders',
    'goods-receipts',
    'team-chat',
    'contracts',
    'leistungsverzeichnis',
];
/**
 * The bookable apps. Hand-written rather than derived from `FEATURE_KEYS`:
 * most keys are tier capabilities and will never be sold separately.
 *
 * The assistant is the first entry. Its capability is already fully wired
 * (rules clause, route guard, `requireFeature` on every chat route) and granted
 * by no tier, so this catalogue entry is the only way to buy it.
 */
exports.APP_CATALOG = [
    {
        appId: 'ai-chat',
        domainId: 'ai',
        order: 0,
        grants: ['aiChat'],
        icon: 'pi pi-comments',
        nameKey: appKey('ai-chat', 'name'),
        shortDescriptionKey: appKey('ai-chat', 'shortDescription'),
        longDescriptionKey: appKey('ai-chat', 'longDescription'),
        highlightKeys: [
            appKey('ai-chat', 'highlights.1'),
            appKey('ai-chat', 'highlights.2'),
            appKey('ai-chat', 'highlights.3'),
        ],
    },
    {
        // PRD-1017/PRD-1412: the KI-Agent — the agent surface that prepares quotes and
        // invoices (ARCH-008 amendments "Agent surfaces" and "An agent surface serves
        // tasks"). Independent of the assistant: a tenant books either or both, and
        // each brings its own allowance entry into the one AI pool.
        appId: 'ai-agent',
        domainId: 'ai',
        order: 1,
        grants: ['aiAgent'],
        icon: 'pi pi-file-edit',
        nameKey: appKey('ai-agent', 'name'),
        shortDescriptionKey: appKey('ai-agent', 'shortDescription'),
        longDescriptionKey: appKey('ai-agent', 'longDescription'),
        highlightKeys: [
            appKey('ai-agent', 'highlights.1'),
            appKey('ai-agent', 'highlights.2'),
            appKey('ai-agent', 'highlights.3'),
        ],
    },
    {
        // PRD-1375: KI-Reporting — a guided management report the model writes
        // about figures the package computed (ARCH-008, AI-written reports). Its
        // own metered path and a flat allowance, like its flat price (BE-006).
        appId: 'ai-reporting',
        domainId: 'ai',
        order: 2,
        grants: ['aiReporting'],
        icon: 'pi pi-chart-line',
        nameKey: appKey('ai-reporting', 'name'),
        shortDescriptionKey: appKey('ai-reporting', 'shortDescription'),
        longDescriptionKey: appKey('ai-reporting', 'longDescription'),
        highlightKeys: [
            appKey('ai-reporting', 'highlights.1'),
            appKey('ai-reporting', 'highlights.2'),
            appKey('ai-reporting', 'highlights.3'),
        ],
    },
    {
        appId: 'external-api',
        domainId: 'integrations',
        order: 0,
        grants: ['externalApi'],
        icon: 'pi pi-code',
        nameKey: appKey('external-api', 'name'),
        shortDescriptionKey: appKey('external-api', 'shortDescription'),
        longDescriptionKey: appKey('external-api', 'longDescription'),
        highlightKeys: [
            appKey('external-api', 'highlights.1'),
            appKey('external-api', 'highlights.2'),
            appKey('external-api', 'highlights.3'),
        ],
    },
    // PRD-852: the finance domain's first app. Deliberately absent from the
    // per-app price tables — the domain is bundle-only, holding any of its apps
    // charges the bundle price, and the domain gesture is the only way to book
    // it (ARCH-006 amendment).
    //
    // It grants the tier modules its export needs beside its own key, for the
    // reason ARCH-002 constraint 7 sanctions (PRD-852 Amendment 1, #1079): the
    // Buchungsstapel posts incoming invoices (`incomingInvoices`) under Kreditoren
    // derived from partner numbers (`partners`), captured automatically (`ocr`,
    // budgeted by the app itself — BE-006). `standard` holds none of the three,
    // and the store promised both sides of the bookkeeping on every paid tier.
    // Changing these grants for tenants that already hold the app needs the
    // holder recompute of ARCH-002 constraint 8: a new, additive entry in the
    // grant-change ledger of `backfill:app-grants`.
    {
        appId: 'tax-advisor-handover',
        domainId: 'finance',
        order: 0,
        grants: ['taxAdvisorHandover', 'incomingInvoices', 'partners', 'ocr'],
        // The store lists the three with "benötigt für den DATEV-Export", so a
        // buyer who already holds them reads why a finance package names them.
        supportingGrants: [
            { key: 'incomingInvoices', reasonKey: appKey('tax-advisor-handover', 'supportingReason') },
            { key: 'partners', reasonKey: appKey('tax-advisor-handover', 'supportingReason') },
            { key: 'ocr', reasonKey: appKey('tax-advisor-handover', 'supportingReason') },
        ],
        icon: 'pi pi-file-export',
        nameKey: appKey('tax-advisor-handover', 'name'),
        shortDescriptionKey: appKey('tax-advisor-handover', 'shortDescription'),
        longDescriptionKey: appKey('tax-advisor-handover', 'longDescription'),
        highlightKeys: [
            appKey('tax-advisor-handover', 'highlights.1'),
            appKey('tax-advisor-handover', 'highlights.2'),
            appKey('tax-advisor-handover', 'highlights.3'),
        ],
    },
    // PRD-856: the finance domain's second app, and the arming switch of the whole
    // Steuerberater-Login — the capability it grants is what every part of that
    // feature is gated on, and no tenant holds it until this entry exists.
    //
    // Price-free like its sibling, and for the same reason: the domain is
    // bundle-only, so holding ANY of its apps charges the full bundle price. Adding
    // this one therefore changes no invoice, which is what makes "free of charge"
    // true by construction rather than by a price of 0 (ARCH-006 / PRD-852).
    {
        appId: 'tax-advisor-access',
        domainId: 'finance',
        order: 1,
        grants: ['taxAdvisorAccess'],
        icon: 'pi pi-id-card',
        nameKey: appKey('tax-advisor-access', 'name'),
        shortDescriptionKey: appKey('tax-advisor-access', 'shortDescription'),
        longDescriptionKey: appKey('tax-advisor-access', 'longDescription'),
        highlightKeys: [
            appKey('tax-advisor-access', 'highlights.1'),
            appKey('tax-advisor-access', 'highlights.2'),
            appKey('tax-advisor-access', 'highlights.3'),
        ],
    },
    // PRD-854: the Warenwirtschaft domain's first app. Price-free like the
    // finance apps and for the same reason — the domain is bundle-only, so the
    // bundle price is the only price, and every Einkauf app joining it later
    // changes no invoice (ARCH-006 amendment).
    {
        appId: 'inventory',
        domainId: 'goods',
        order: 0,
        grants: ['inventory'],
        icon: 'pi pi-box',
        nameKey: appKey('inventory', 'name'),
        shortDescriptionKey: appKey('inventory', 'shortDescription'),
        longDescriptionKey: appKey('inventory', 'longDescription'),
        highlightKeys: [
            appKey('inventory', 'highlights.1'),
            appKey('inventory', 'highlights.2'),
            appKey('inventory', 'highlights.3'),
        ],
    },
    // PRD-1845, re-cut by issue #1013: the Einkauf half of the Warenwirtschaft is
    // three apps, not one. It was modelled as a single `purchasing` tile while it
    // was one PRD; on the domain card that made a bundle containing four areas
    // look like a bundle containing two, and the card is the only place a buyer
    // ever sees what the one price covers.
    //
    // Splitting it costs nothing in the purchase path: the domain is bundle-only,
    // so the domain — never an app — is the purchase unit, every one of these is
    // price-free, and holding any of them charges the same 9 €. What changes is
    // only what the store SHOWS.
    //
    // All three grant `partners`, `incomingInvoices` and `ocr` beside their own
    // key, for the reason ARCH-002 constraint 7 sanctions: a Lieferant IS a
    // partner, the Wareneingang's invoice check reads the Eingangsrechnung, and an
    // Eingangsrechnung always comes with its OCR (the catalogue invariant of issue
    // #1097, budgeted by the app itself — BE-006). `standard` holds none of the
    // three, and each app must grant what its own function requires rather than
    // lean on a sibling being installed. Changing these grants for tenants that
    // already hold the apps needs the holder recompute of ARCH-002 constraint 8.
    {
        // First in the chain and first on the card: a purchase begins by asking what
        // it costs, the same order the sidebar lists these in.
        appId: 'purchase-requests',
        domainId: 'goods',
        order: 1,
        grants: ['procurement', 'partners', 'incomingInvoices', 'ocr'],
        icon: 'pi pi-comments',
        nameKey: appKey('purchase-requests', 'name'),
        shortDescriptionKey: appKey('purchase-requests', 'shortDescription'),
        longDescriptionKey: appKey('purchase-requests', 'longDescription'),
        highlightKeys: [
            appKey('purchase-requests', 'highlights.1'),
            appKey('purchase-requests', 'highlights.2'),
            appKey('purchase-requests', 'highlights.3'),
        ],
    },
    {
        appId: 'purchase-orders',
        domainId: 'goods',
        order: 2,
        grants: ['procurement', 'partners', 'incomingInvoices', 'ocr'],
        icon: 'pi pi-shopping-cart',
        nameKey: appKey('purchase-orders', 'name'),
        shortDescriptionKey: appKey('purchase-orders', 'shortDescription'),
        longDescriptionKey: appKey('purchase-orders', 'longDescription'),
        highlightKeys: [
            appKey('purchase-orders', 'highlights.1'),
            appKey('purchase-orders', 'highlights.2'),
            appKey('purchase-orders', 'highlights.3'),
        ],
    },
    {
        appId: 'goods-receipts',
        domainId: 'goods',
        order: 3,
        grants: ['procurement', 'partners', 'incomingInvoices', 'ocr'],
        icon: 'pi pi-download',
        nameKey: appKey('goods-receipts', 'name'),
        shortDescriptionKey: appKey('goods-receipts', 'shortDescription'),
        longDescriptionKey: appKey('goods-receipts', 'longDescription'),
        highlightKeys: [
            appKey('goods-receipts', 'highlights.1'),
            appKey('goods-receipts', 'highlights.2'),
            appKey('goods-receipts', 'highlights.3'),
        ],
    },
    {
        // PRD-1632: Team-Chat — direct messages and groups between the members of
        // one tenant (DATA-036). Grants its own capability and nothing in support:
        // chatting needs no other module.
        appId: 'team-chat',
        domainId: 'crm',
        order: 0,
        grants: ['teamChat'],
        icon: 'pi pi-send',
        nameKey: appKey('team-chat', 'name'),
        shortDescriptionKey: appKey('team-chat', 'shortDescription'),
        longDescriptionKey: appKey('team-chat', 'longDescription'),
        highlightKeys: [
            appKey('team-chat', 'highlights.1'),
            appKey('team-chat', 'highlights.2'),
            appKey('team-chat', 'highlights.3'),
        ],
        // DATA-036 rule 8: the conversations go ninety days after the app ends.
        cancellationNoteKey: appKey('team-chat', 'cancellationNote'),
    },
    {
        // PRD-1206: Verträge — sold flat per workspace with an included monthly
        // allowance of signing requests. It grants `contracts` alone: reading
        // contracts stays on crm / partners, which every buyer already holds.
        appId: 'contracts',
        domainId: 'crm',
        order: 1,
        grants: ['contracts'],
        icon: 'pi pi-file-edit',
        nameKey: appKey('contracts', 'name'),
        shortDescriptionKey: appKey('contracts', 'shortDescription'),
        longDescriptionKey: appKey('contracts', 'longDescription'),
        highlightKeys: [
            appKey('contracts', 'highlights.1'),
            appKey('contracts', 'highlights.2'),
            appKey('contracts', 'highlights.3'),
        ],
    },
    {
        // PRD-2133: the Leistungsverzeichnis — a connected app (ARCH-013). Its data
        // and its roles belong to the connected service; ZETL sells it, assigns the
        // roles and carries the requests.
        appId: 'leistungsverzeichnis',
        domainId: 'construction',
        order: 0,
        grants: ['leistungsverzeichnis'],
        connected: { audience: 'connected-service-poc' },
        icon: 'pi pi-list-check',
        nameKey: appKey('leistungsverzeichnis', 'name'),
        shortDescriptionKey: appKey('leistungsverzeichnis', 'shortDescription'),
        longDescriptionKey: appKey('leistungsverzeichnis', 'longDescription'),
        highlightKeys: [
            appKey('leistungsverzeichnis', 'highlights.1'),
            appKey('leistungsverzeichnis', 'highlights.2'),
            appKey('leistungsverzeichnis', 'highlights.3'),
        ],
    },
];
/** Type guard: is `value` a bookable app id? */
function isAppId(value) {
    return typeof value === 'string' && exports.APP_IDS.includes(value);
}
/** The catalogue entry for an app id, or `undefined` for an unknown one. */
function appById(appId) {
    return exports.APP_CATALOG.find((app) => app.appId === appId);
}
/**
 * The capability keys granted by a set of installed app ids.
 *
 * Unknown ids are IGNORED rather than rejected: an app withdrawn from the
 * catalogue while tenants still hold it must fail closed on capabilities (the
 * tenant loses the capability) rather than throwing somewhere deep inside a
 * feature resolution that every request runs. Cleaning up such an id is
 * operational work, not something this function may decide.
 */
function featuresFromApps(installedApps) {
    const granted = new Set();
    for (const appId of installedApps) {
        for (const key of appById(appId)?.grants ?? []) {
            granted.add(key);
        }
    }
    return [...granted];
}
/**
 * The reason a set of apps grants `key` only in support of its own function,
 * or `null` when it does not: no app of the set grants the key, or one of
 * them grants it for its own sake (then the key is simply part of what the set
 * sells, and no "benötigt für …" applies).
 */
function supportingGrantReason(appIds, key) {
    let reason = null;
    for (const appId of appIds) {
        const app = appById(appId);
        if (!app?.grants.includes(key))
            continue;
        const supporting = app.supportingGrants?.find((grant) => grant.key === key);
        if (!supporting)
            return null;
        reason ??= supporting.reasonKey;
    }
    return reason;
}
//# sourceMappingURL=app-catalog.js.map